import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTeacher } from '@/context/TeacherContext';
import { useToast } from '@/hooks/use-toast';
import { FileText, Download, Wand2, BookOpen, Save, ArrowLeft } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn, getButtonColor } from '@/lib/utils';
import axios from 'axios';
import { apiUrl } from '@/api/config';
import { getAuthToken } from '@/utils/auth';

export default function QuestionGenerator() {
  const navigate = useNavigate();
  const { teacherTheme, isDark } = useTeacher();
  const { toast } = useToast();
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [isLoadingSubjects, setIsLoadingSubjects] = useState(true);
  const [generatorSettings, setGeneratorSettings] = useState({
    subjectId: null,
    subject: '',
    chapterId: null,
    chapter: '',
    difficulty: 'medium',
    questionType: 'MCQ',
    count: 10
  });
  const [generatedQuestions, setGeneratedQuestions] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [recentlyGenerated, setRecentlyGenerated] = useState([]);
  const [isLoadingRecent, setIsLoadingRecent] = useState(true);

  // Fetch subjects and chapters on mount
  useEffect(() => {
    const fetchSubjectsChapters = async () => {
      setIsLoadingSubjects(true);
      try {
        const token = getAuthToken('teacher');
        if (!token) return;
        
        const res = await axios.get(apiUrl('v1/teachers/ai-tools/subjects-chapters/'), {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const apiSubjects = res?.data?.subjects || [];
        setSubjects(apiSubjects);
        
        // Set first subject and its chapters if available
        if (apiSubjects.length > 0) {
          const firstSubject = apiSubjects[0];
          setGeneratorSettings(prev => ({
            ...prev,
            subjectId: firstSubject.subject_id,
            subject: firstSubject.subject_name
          }));
          setChapters(firstSubject.chapters || []);
          
          if (firstSubject.chapters && firstSubject.chapters.length > 0) {
            const firstChapter = firstSubject.chapters[0];
            setGeneratorSettings(prev => ({
              ...prev,
              chapterId: firstChapter.chapter_id,
              chapter: firstChapter.chapter_name
            }));
          }
        }
      } catch (err) {
        console.error('Failed to load subjects and chapters', err);
        toast({
          title: "Error",
          description: "Failed to load subjects and chapters. Please refresh the page.",
          variant: "destructive",
        });
      } finally {
        setIsLoadingSubjects(false);
      }
    };
    
    fetchSubjectsChapters();
  }, [toast]);

  // Fetch recently generated questions from library
  useEffect(() => {
    const fetchRecentlyGenerated = async () => {
      setIsLoadingRecent(true);
      try {
        const token = getAuthToken('teacher');
        if (!token) return;

        // Fetch recent questions (limited to 20 to group them)
        const res = await axios.get(apiUrl('v1/teachers/question-library/'), {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            page: 1,
            page_size: 20
          }
        });

        const questions = res?.data?.questions || [];
        
        // Group questions by subject, chapter, question_type, and created date
        const groupedMap = new Map();
        
        questions.forEach((q) => {
          const createdDate = q.created_at ? new Date(q.created_at).toISOString().split('T')[0] : '';
          const key = `${q.subject_id}-${q.chapter_id}-${q.question_type}-${createdDate}`;
          
          if (!groupedMap.has(key)) {
            const typeDisplayMap = {
              'mcq': 'MCQ',
              'true_false': 'True/False',
              'short_answer': 'Short Answer',
              'written_detailed': 'Written Detailed'
            };
            
            groupedMap.set(key, {
              id: q.id,
              subject: q.subject_name,
              chapter: q.chapter_name,
              question_type: q.question_type,
              type_display: typeDisplayMap[q.question_type] || q.question_type,
              difficulty: q.difficulty,
              date: createdDate,
              count: 0,
              questions: []
            });
          }
          
          const group = groupedMap.get(key);
          group.count++;
          group.questions.push(q);
        });
        
        // Convert map to array and sort by date (most recent first)
        const grouped = Array.from(groupedMap.values())
          .sort((a, b) => new Date(b.date) - new Date(a.date))
          .slice(0, 5) // Limit to 5 most recent groups
          .map((group, idx) => ({
            id: group.id || idx + 1,
            title: `${group.subject} - ${group.chapter}`,
            type: group.type_display,
            count: group.count,
            difficulty: group.difficulty.charAt(0).toUpperCase() + group.difficulty.slice(1),
            date: group.date ? new Date(group.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '',
            questions: group.questions
          }));
        
        setRecentlyGenerated(grouped);
      } catch (err) {
        console.error('Failed to load recently generated questions', err);
        setRecentlyGenerated([]);
      } finally {
        setIsLoadingRecent(false);
      }
    };

    fetchRecentlyGenerated();
  }, [generatedQuestions]); // Re-fetch when new questions are generated

  // Update chapters when subject changes
  const handleSubjectChange = (subjectId) => {
    const selectedSubject = subjects.find(s => s.subject_id === subjectId);
    if (selectedSubject) {
      setGeneratorSettings(prev => ({
        ...prev,
        subjectId: selectedSubject.subject_id,
        subject: selectedSubject.subject_name,
        chapterId: null,
        chapter: ''
      }));
      setChapters(selectedSubject.chapters || []);
      
      // Auto-select first chapter if available
      if (selectedSubject.chapters && selectedSubject.chapters.length > 0) {
        const firstChapter = selectedSubject.chapters[0];
        setGeneratorSettings(prev => ({
          ...prev,
          chapterId: firstChapter.chapter_id,
          chapter: firstChapter.chapter_name
        }));
      }
    }
  };

  const handleChapterChange = (chapterId) => {
    const selectedChapter = chapters.find(c => c.chapter_id === chapterId);
    if (selectedChapter) {
      setGeneratorSettings(prev => ({
        ...prev,
        chapterId: selectedChapter.chapter_id,
        chapter: selectedChapter.chapter_name
      }));
    }
  };

  // Generate questions handler
  const handleGenerateQuestions = async () => {
    if (!generatorSettings.subjectId || !generatorSettings.chapterId) {
      toast({
        title: "Error",
        description: "Please select both subject and chapter",
        variant: "destructive",
      });
      return;
    }

    setIsGenerating(true);
    try {
      const token = getAuthToken('teacher');
      if (!token) {
        toast({
          title: "Error",
          description: "Authentication required",
          variant: "destructive",
        });
        return;
      }

      // Map question type from frontend display to backend API format
      const questionTypeMap = {
        'MCQ': 'MQP',
        'mcq': 'MQP',  // Handle lowercase variant
        'MQP': 'MQP',
        'True/False': 'True/False',
        'Short Answers': 'Short Answers',
        'Written Detailed': 'Written Detailed',
        'Mixed': 'Mixed'
      };
      const apiQuestionType = questionTypeMap[generatorSettings.questionType] || generatorSettings.questionType;
      
      if (!apiQuestionType || !questionTypeMap[generatorSettings.questionType]) {
        toast({
          title: "Error",
          description: `Invalid question type: ${generatorSettings.questionType}. Please select a valid type.`,
          variant: "destructive",
        });
        setIsGenerating(false);
        return;
      }

      // Map difficulty from frontend to backend
      const difficultyMap = {
        'easy': 'easy',
        'medium': 'moderate',
        'hard': 'hard'
      };
      const apiDifficulty = difficultyMap[generatorSettings.difficulty] || generatorSettings.difficulty;

      const res = await axios.post(
        apiUrl('v1/teachers/ai-tools/generate/'),
        {
          subject_id: generatorSettings.subjectId,
          chapter_id: generatorSettings.chapterId,
          question_type: apiQuestionType,
          num_questions: generatorSettings.count,
          difficulty: apiDifficulty
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 120000 // 2 minutes timeout for question generation
        }
      );

      const apiQuestions = res?.data?.questions || [];
      
      // Transform API response to match existing preview format
      const transformedQuestions = apiQuestions.map((q, idx) => {
        const baseQuestion = {
          id: q.id || idx + 1,
          type: q.question_type,
          question: q.question_text,
          difficulty: q.difficulty,
          correctAnswer: q.correct_option || q.correct_answer,
          correctAnswerText: q.correct_answer,
          explanation: q.explanation || ''
        };

        // Add type-specific fields
        if (q.question_type === 'mcq' || q.question_type === 'true_false') {
          baseQuestion.options = q.options ? Object.values(q.options) : [];
          baseQuestion.correctAnswer = q.correct_option;
        }

        return baseQuestion;
      });

      setGeneratedQuestions(transformedQuestions);
      
      toast({
        title: "Questions Generated",
        description: `Generated ${transformedQuestions.length} ${generatorSettings.difficulty} ${generatorSettings.questionType} questions successfully`,
      });
    } catch (err) {
      console.error('Failed to generate questions', err);
      toast({
        title: "Error",
        description: err?.response?.data?.error || "Failed to generate questions. Please try again.",
        variant: "destructive",
      });
      setGeneratedQuestions([]);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRegenerate = () => {
    handleGenerateQuestions();
  };

  const handleSaveToLibrary = async () => {
    if (generatedQuestions.length === 0) {
      toast({
        title: "Error",
        description: "No questions to save. Please generate questions first.",
        variant: "destructive",
      });
      return;
    }

    if (isSaving) {
      // Prevent duplicate saves
      return;
    }

    setIsSaving(true);
    try {
      const token = getAuthToken('teacher');
      if (!token) {
        toast({
          title: "Error",
          description: "Authentication required",
          variant: "destructive",
        });
        setIsSaving(false);
        return;
      }

      // Save each question to library
      const savePromises = generatedQuestions.map(async (q) => {
        const questionData = {
          subject_id: generatorSettings.subjectId,
          chapter_id: generatorSettings.chapterId,
          question_text: q.question,
          question_type: q.type,
          difficulty: q.difficulty || generatorSettings.difficulty,
          options: q.options ? (Array.isArray(q.options) ? 
            Object.fromEntries(q.options.map((opt, idx) => [String.fromCharCode(65 + idx), opt])) :
            q.options) : null,
          correct_option: q.correctAnswer && (q.type === 'mcq' || q.type === 'true_false') ? q.correctAnswer : null,
          correct_answer: q.correctAnswerText || q.correctAnswer || '',
          explanation: q.explanation || ''
        };

        return axios.post(
          apiUrl('v1/teachers/question-library/'),
          questionData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      });

      await Promise.all(savePromises);

      const savedCount = generatedQuestions.length;
      
      // Clear generated questions after successful save
      setGeneratedQuestions([]);

      toast({
        title: "Saved Successfully",
        description: `${savedCount} questions saved to library. Visit the library to access them.`,
      });
    } catch (err) {
      console.error('Failed to save questions to library', err);
      toast({
        title: "Error",
        description: err?.response?.data?.error || "Failed to save questions to library. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownloadQuestions = () => {
    if (generatedQuestions.length === 0) {
      toast({
        title: "Error",
        description: "No questions to download. Please generate questions first.",
        variant: "destructive",
      });
      return;
    }

    // Build the download content
    const subjectChapter = `${generatorSettings.subject} - ${generatorSettings.chapter}`;
    let content = `${subjectChapter}\n${'='.repeat(50)}\n\n`;
    content += `Subject: ${generatorSettings.subject}\n`;
    content += `Chapter: ${generatorSettings.chapter}\n`;
    content += `Difficulty: ${generatorSettings.difficulty}\n`;
    content += `Type: ${generatorSettings.questionType}\n`;
    content += `Count: ${generatedQuestions.length}\n\n`;
    content += `${'='.repeat(50)}\n\n`;

    // Add each question
    generatedQuestions.forEach((q, idx) => {
      content += `Question ${idx + 1}:\n`;
      content += `${q.question}\n\n`;

      // Add options for MCQ/True-False
      if ((q.type === 'mcq' || q.type === 'true_false') && q.options && q.options.length > 0) {
        content += `Options:\n`;
        q.options.forEach((option, optIdx) => {
          content += `${String.fromCharCode(65 + optIdx)}) ${option}\n`;
        });
        content += `\n`;
      }

      // Add correct answer
      content += `Correct Answer: ${q.correctAnswerText || q.correctAnswer || 'N/A'}\n`;

      // Add explanation if available
      if (q.explanation) {
        content += `Explanation: ${q.explanation}\n`;
      }

      content += `\n${'-'.repeat(50)}\n\n`;
    });

    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const fileName = `${subjectChapter.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.txt`;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    toast({
      title: "Downloaded",
      description: "Questions downloaded successfully",
    });
  };

  const handleDownloadRecentQuestions = (item) => {
    // Create downloadable content from the grouped questions
    const questionsContent = item.questions.map((q, idx) => {
      let content = `Question ${idx + 1}: ${q.question_text}\n`;
      
      if (q.options && typeof q.options === 'object' && !Array.isArray(q.options)) {
        const options = Object.entries(q.options);
        content += `Options:\n`;
        options.forEach(([key, value]) => {
          content += `${key}) ${value}\n`;
        });
      } else if (Array.isArray(q.options)) {
        content += `Options:\n`;
        q.options.forEach((opt, optIdx) => {
          content += `${String.fromCharCode(65 + optIdx)}) ${opt}\n`;
        });
      }
      
      content += `Correct Answer: ${q.correct_option || q.correct_answer}\n`;
      if (q.explanation) {
        content += `Explanation: ${q.explanation}\n`;
      }
      content += `\n`;
      return content;
    }).join('\n');

    const fullContent = `${item.title} - ${item.count} questions\nGenerated on: ${item.date}\n\n${questionsContent}`;

    // Create blob and download
    const blob = new Blob([fullContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${item.title.replace(/\s+/g, '_')}_${item.date.replace(/,/g, '')}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    toast({
      title: "Downloaded",
      description: "Questions downloaded successfully",
    });
  };

  return (
    <div className={cn("flex flex-col h-full p-6 overflow-y-auto", isDark ? "bg-gradient-to-br from-[hsl(var(--background))] via-[hsl(var(--card))] to-[hsl(var(--background))]" : "bg-gradient-to-br from-[#F8F5F0] via-[#F5F1EA] to-[#F2EBE0]")}>
      <div className="max-w-7xl mx-auto w-full space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/teacher/ai-tools')}
              className={cn("rounded-xl", isDark ? "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--card-border))]/60" : "text-[#6B6B6B] hover:text-[#1A1A1A] hover:bg-[#F0EBE3]/60")}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className={cn("text-4xl font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>Question Generator</h1>
              <p className={cn("mt-1 text-sm font-normal", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#8B7B6B]")}>Generate AI-powered questions for your curriculum</p>
            </div>
          </div>
          
          <Button
            onClick={() => navigate('/teacher/library')}
            className={cn("rounded-xl shadow-sm flex-shrink-0", getButtonColor(isDark))}
            title="Go to Question Library"
          >
            <BookOpen className="w-6 h-6 mr-2" />
            <span className="text-sm font-medium">Library</span>
          </Button>
        </div>

        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Settings Panel */}
            <div className="lg:col-span-1">
              <Card className={cn("border shadow-sm", isDark ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))]" : "bg-white border-[#E0D4BF]")}>
                <CardHeader className={cn("rounded-t-2xl border-b", isDark ? "bg-[hsl(var(--card-border))]/10 border-[hsl(var(--card-border))]" : "bg-[#F8F5F0] border-[#E0D4BF]")}>
                  <CardTitle className={cn("text-lg", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Generator Settings</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div>
                      <label className={cn("block text-base font-bold mb-2", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Subject</label>
                      <select
                        value={generatorSettings.subjectId || ''}
                        onChange={(e) => handleSubjectChange(parseInt(e.target.value))}
                        disabled={isLoadingSubjects}
                        className={cn("w-full px-3 py-2 rounded-lg focus:ring-2 focus:border-transparent outline-none", isDark ? "bg-[hsl(var(--background))] border border-[hsl(var(--card-border))] text-[hsl(var(--foreground))] focus:ring-[hsl(var(--gold-primary))]" : "bg-white border border-[#E0D4BF] text-[#2B2B2B] focus:ring-[#C9A961]")}
                      >
                        {isLoadingSubjects ? (
                          <option>Loading subjects...</option>
                        ) : subjects.length === 0 ? (
                          <option>No subjects assigned</option>
                        ) : (
                          <>
                            <option value="">Select Subject</option>
                            {subjects.map((subject) => (
                              <option key={subject.subject_id} value={subject.subject_id}>
                                {subject.subject_name}
                              </option>
                            ))}
                          </>
                        )}
                      </select>
                    </div>

                    <div>
                      <label className={cn("block text-base font-bold mb-2", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Chapter</label>
                      <select
                        value={generatorSettings.chapterId || ''}
                        onChange={(e) => handleChapterChange(parseInt(e.target.value))}
                        disabled={!generatorSettings.subjectId || chapters.length === 0}
                        className={cn("w-full px-3 py-2 rounded-lg focus:ring-2 focus:border-transparent outline-none", isDark ? "bg-[hsl(var(--background))] border border-[hsl(var(--card-border))] text-[hsl(var(--foreground))] focus:ring-[hsl(var(--gold-primary))]" : "bg-white border border-[#E0D4BF] text-[#2B2B2B] focus:ring-[#C9A961]")}
                      >
                        {!generatorSettings.subjectId ? (
                          <option>Select subject first</option>
                        ) : chapters.length === 0 ? (
                          <option>No chapters available</option>
                        ) : (
                          <>
                            <option value="">Select Chapter</option>
                            {chapters.map((chapter) => (
                              <option key={chapter.chapter_id} value={chapter.chapter_id}>
                                {chapter.chapter_name}
                              </option>
                            ))}
                          </>
                        )}
                      </select>
                    </div>

                    <div>
                      <label className={cn("block text-base font-bold mb-2", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Difficulty</label>
                      <div className="space-y-2">
                        {['easy', 'medium', 'hard'].map((level) => (
                          <label key={level} className="flex items-center cursor-pointer">
                            <input
                              type="radio"
                              name="difficulty"
                              value={level}
                              checked={generatorSettings.difficulty === level}
                              onChange={(e) => setGeneratorSettings({ ...generatorSettings, difficulty: e.target.value })}
                              className={cn("mr-2", isDark ? "accent-[hsl(var(--gold-primary))]" : "accent-[#C9A961]")}
                            />
                            <span className={cn("text-sm capitalize", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>{level}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className={cn("block text-base font-bold mb-2", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Question Type</label>
                      <select
                        value={generatorSettings.questionType}
                        onChange={(e) => setGeneratorSettings({ ...generatorSettings, questionType: e.target.value })}
                        className={cn("w-full px-3 py-2 rounded-lg focus:ring-2 focus:border-transparent outline-none", isDark ? "bg-[hsl(var(--background))] border border-[hsl(var(--card-border))] text-[hsl(var(--foreground))] focus:ring-[hsl(var(--gold-primary))]" : "bg-white border border-[#E0D4BF] text-[#2B2B2B] focus:ring-[#C9A961]")}
                      >
                        <option value="MCQ">MCQ</option>
                        <option value="True/False">True/False</option>
                        <option value="Short Answers">Short Answers</option>
                        <option value="Written Detailed">Written Detailed</option>
                        <option value="Mixed">Mixed</option>
                      </select>
                    </div>

                    <div>
                      <label className={cn("block text-base font-bold mb-2", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Number of Questions</label>
                      <input
                        type="number"
                        value={generatorSettings.count}
                        onChange={(e) => setGeneratorSettings({ ...generatorSettings, count: parseInt(e.target.value) })}
                        className={cn("w-full px-3 py-2 rounded-lg focus:ring-2 focus:border-transparent outline-none", isDark ? "bg-[hsl(var(--background))] border border-[hsl(var(--card-border))] text-[hsl(var(--foreground))] focus:ring-[hsl(var(--gold-primary))]" : "bg-white border border-[#E0D4BF] text-[#2B2B2B] focus:ring-[#C9A961]")}
                        min="1"
                        max="50"
                      />
                    </div>

                    <Button 
                      onClick={handleGenerateQuestions}
                      disabled={isGenerating || !generatorSettings.subjectId || !generatorSettings.chapterId}
                      className={cn("w-full shadow-md hover:shadow-lg transition-all h-10 font-medium rounded-lg", getButtonColor(isDark))}>
                      <Wand2 className={cn("w-4 h-4 mr-2", isGenerating && "animate-spin")} />
                      {isGenerating ? 'Generating...' : 'Generate Questions'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Preview Panel */}
            <div className="lg:col-span-2">
              <Card className={cn("border shadow-sm", isDark ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))]" : "bg-white border-[#E0D4BF]")}>
                <CardHeader className={cn("rounded-t-2xl border-b", isDark ? "bg-[hsl(var(--card-border))]/10 border-[hsl(var(--card-border))]" : "bg-[#F8F5F0] border-[#E0D4BF]")}>
                  <CardTitle className={cn("text-lg", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Preview</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  {isGenerating ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="text-center">
                        <Wand2 className={cn("w-12 h-12 mx-auto mb-4 animate-pulse", isDark ? "text-[hsl(var(--gold-primary))]" : "text-[#C9A961]")} />
                        <p className={cn("text-base font-medium", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Generating questions...</p>
                      </div>
                    </div>
                  ) : generatedQuestions.length === 0 ? (
                    <div className="text-center py-12">
                      <FileText className={cn("w-16 h-16 mx-auto mb-4 opacity-50", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")} />
                      <p className={cn("text-base font-medium mb-2", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>No questions generated yet</p>
                      <p className={cn("text-sm", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>Click "Generate Questions" to create your questionnaire</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {generatedQuestions.map((q) => (
                        <div key={q.id} className={cn("border rounded-lg p-4", isDark ? "bg-[hsl(var(--background))] border-[hsl(var(--card-border))]" : "bg-[#F8F5F0] border-[#E0D4BF]")}>
                          <div className="flex items-center justify-between mb-2">
                            <span className={cn("text-base font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Question {q.id}</span>
                            <span className={cn("text-sm font-medium capitalize", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>{q.difficulty} Difficulty</span>
                          </div>
                          
                          {/* MCQ Type */}
                          {q.type === 'mcq' && (
                            <>
                              <p className={cn("mb-3", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>{q.question}</p>
                              <div className="space-y-2">
                                {q.options && q.options.map((option, index) => (
                                  <label key={index} className="flex items-center cursor-pointer">
                                    <input type="radio" name={`q${q.id}`} className={cn("mr-2", isDark ? "accent-[hsl(var(--gold-primary))]" : "accent-[#C9A961]")} />
                                    <span className={cn("text-sm", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>{String.fromCharCode(65 + index)}) {option}</span>
                                  </label>
                                ))}
                              </div>
                              <div className={cn("mt-3 pt-3 border-t", isDark ? "border-[hsl(var(--card-border))]" : "border-[#E0D4BF]")}>
                                <div className="space-y-2">
                                  <div className="flex justify-between items-center text-sm">
                                    <span className={cn(isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>Correct Answer: <span className="font-semibold text-green-400">{q.correctAnswer}</span></span>
                                  </div>
                                  {q.correctAnswerText && (
                                    <p className={cn("text-sm", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                                      <span className="font-semibold">Explanation: </span>{q.correctAnswerText}
                                    </p>
                                  )}
                                  {q.explanation && (
                                    <p className={cn("text-sm italic", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>{q.explanation}</p>
                                  )}
                                </div>
                              </div>
                            </>
                          )}

                          {/* True/False Type */}
                          {q.type === 'true_false' && (
                            <>
                              <p className={cn("mb-3", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>{q.question}</p>
                              <div className="space-y-2">
                                {q.options && q.options.map((option, index) => (
                                  <label key={index} className="flex items-center cursor-pointer">
                                    <input type="radio" name={`q${q.id}`} className={cn("mr-2", isDark ? "accent-[hsl(var(--gold-primary))]" : "accent-[#C9A961]")} />
                                    <span className={cn("text-sm", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>{String.fromCharCode(65 + index)}) {option}</span>
                                  </label>
                                ))}
                              </div>
                              <div className={cn("mt-3 pt-3 border-t", isDark ? "border-[hsl(var(--card-border))]" : "border-[#E0D4BF]")}>
                                <div className="space-y-2">
                                  <div className="flex justify-between items-center text-sm">
                                    <span className={cn(isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>Correct Answer: <span className="font-semibold text-green-400">{q.correctAnswer}</span></span>
                                  </div>
                                  {q.correctAnswerText && (
                                    <p className={cn("text-sm", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                                      <span className="font-semibold">Explanation: </span>{q.correctAnswerText}
                                    </p>
                                  )}
                                  {q.explanation && (
                                    <p className={cn("text-sm italic", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>{q.explanation}</p>
                                  )}
                                </div>
                              </div>
                            </>
                          )}

                          {/* Short Answer Type */}
                          {(q.type === 'short' || q.type === 'short_answer') && (
                            <>
                              <p className={cn("mb-3", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>{q.question}</p>
                              <div className={cn("rounded p-3", isDark ? "bg-[hsl(var(--card-border))]/10" : "bg-white border border-[#E0D4BF]")}>
                                <textarea
                                  placeholder="Your answer here (2-3 sentences)..."
                                  className={cn("w-full h-20 border-0 bg-transparent resize-none focus:outline-none placeholder:text-muted-foreground", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}
                                />
                              </div>
                              <div className={cn("mt-3 pt-3 border-t", isDark ? "border-[hsl(var(--card-border))]" : "border-[#E0D4BF]")}>
                                <div className="space-y-2">
                                  {q.correctAnswerText && (
                                    <>
                                      <p className={cn("text-sm font-semibold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Correct Answer:</p>
                                      <p className={cn("text-sm", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>{q.correctAnswerText}</p>
                                    </>
                                  )}
                                  {q.explanation && (
                                    <p className={cn("text-sm italic", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>{q.explanation}</p>
                                  )}
                                </div>
                              </div>
                            </>
                          )}

                          {/* Written Detailed Type */}
                          {(q.type === 'long' || q.type === 'written_detailed') && (
                            <>
                              <p className={cn("mb-3", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>{q.question}</p>
                              <div className={cn("rounded p-3", isDark ? "bg-[hsl(var(--card-border))]/10" : "bg-white border border-[#E0D4BF]")}>
                                <textarea
                                  placeholder="Your detailed answer here (paragraph format)..."
                                  className={cn("w-full h-32 border-0 bg-transparent resize-none focus:outline-none placeholder:text-muted-foreground", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}
                                />
                              </div>
                              <div className={cn("mt-3 pt-3 border-t", isDark ? "border-[hsl(var(--card-border))]" : "border-[#E0D4BF]")}>
                                <div className="space-y-2">
                                  {q.correctAnswerText && (
                                    <>
                                      <p className={cn("text-sm font-semibold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Correct Answer:</p>
                                      <p className={cn("text-sm", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>{q.correctAnswerText}</p>
                                    </>
                                  )}
                                  {q.explanation && (
                                    <p className={cn("text-sm italic", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>{q.explanation}</p>
                                  )}
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {generatedQuestions.length > 0 && (
                    <div className="mt-6 flex space-x-3">
                      <Button 
                        onClick={handleRegenerate}
                        disabled={isGenerating}
                        className={cn("flex-1 shadow-md hover:shadow-lg transition-all h-10 font-medium rounded-lg", getButtonColor(isDark))}
                      >
                        <Wand2 className="w-4 h-4 mr-2" />
                        Regenerate
                      </Button>
                      <Button
                        onClick={handleSaveToLibrary}
                        disabled={isGenerating || isSaving || generatedQuestions.length === 0}
                        className={cn("flex-1 shadow-md hover:shadow-lg transition-all h-10 font-medium rounded-lg", getButtonColor(isDark))}
                      >
                        <Save className={cn("w-4 h-4 mr-2", isSaving && "animate-spin")} />
                        {isSaving ? 'Saving...' : 'Save to Library'}
                      </Button>
                      <Button
                        onClick={handleDownloadQuestions}
                        disabled={isGenerating || generatedQuestions.length === 0}
                        className={cn("flex-1 shadow-md hover:shadow-lg transition-all h-10 font-medium rounded-lg", getButtonColor(isDark))}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Recently Generated */}
          <Card className={cn("border shadow-sm", isDark ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))]" : "bg-white border-[#E0D4BF]")}>
            <CardHeader className={cn("rounded-t-2xl border-b", isDark ? "bg-[hsl(var(--card-border))]/10 border-[hsl(var(--card-border))]" : "bg-[#F8F5F0] border-[#E0D4BF]")}>
              <CardTitle className={cn("text-lg", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Recently Generated</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {isLoadingRecent ? (
                <div className="text-center py-8">
                  <p className={cn("text-sm", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>Loading recently generated questions...</p>
                </div>
              ) : recentlyGenerated.length === 0 ? (
                <div className="text-center py-8">
                  <p className={cn("text-sm", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>No recently generated questions. Generate questions to see them here.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentlyGenerated.map((item) => (
                    <div key={item.id} className={cn("flex items-center justify-between p-4 rounded-lg border", isDark ? "bg-[hsl(var(--background))] border-[hsl(var(--card-border))]" : "bg-[#F8F5F0] border-[#E0D4BF]")}>
                      <div className="flex-1">
                        <h4 className={cn("font-bold text-base", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>{item.title}</h4>
                        <p className={cn("text-sm font-medium", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>{item.type} • {item.count} questions • {item.difficulty}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={cn("text-sm font-medium", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>{item.date}</span>
                        <Button 
                          onClick={() => handleDownloadRecentQuestions(item)}
                          size="sm" className={cn("shadow-sm transition-all h-8 text-xs font-medium rounded-lg", getButtonColor(isDark))}>
                          <Download className="w-4 h-4 mr-1" />
                          Download
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

